var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _intersectionObserver, _mutationObserver, _cachedRecommendations, _activeFetch, _ProductRecommendations_instances, loadRecommendations_fn, fetchCachedRecommendations_fn, handleError_fn;
import { Component } from "@theme/component";
import { morphSection } from "@theme/section-renderer";
class ProductRecommendations extends Component {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ProductRecommendations_instances);
    /**
     * The observer for the product recommendations
     * @type {IntersectionObserver}
     */
    __privateAdd(this, _intersectionObserver, new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        __privateMethod(this, _ProductRecommendations_instances, loadRecommendations_fn).call(this);
      },
      { rootMargin: "0px 0px 400px 0px" }
    ));
    /**
     * Observing changes to the elements attributes
     * @type {MutationObserver}
     */
    __privateAdd(this, _mutationObserver, new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.target !== this || mutation.type !== "attributes") continue;
        if (mutation.attributeName === "data-error") continue;
        if (mutation.attributeName === "class" && this.classList.contains("hidden")) continue;
        if (mutation.attributeName === "data-recommendations-performed" && this.dataset.recommendationsPerformed === "true")
          continue;
        __privateMethod(this, _ProductRecommendations_instances, loadRecommendations_fn).call(this);
        break;
      }
    }));
    /**
     * The cached recommendations
     * @type {Record<string, string>}
     */
    __privateAdd(this, _cachedRecommendations, {});
    /**
     * An abort controller for the active fetch (if there is one)
     * @type {AbortController | null}
     */
    __privateAdd(this, _activeFetch, null);
  }
  connectedCallback() {
    super.connectedCallback();
    __privateGet(this, _intersectionObserver).observe(this);
    __privateGet(this, _mutationObserver).observe(this, { attributes: true });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    __privateGet(this, _intersectionObserver).disconnect();
    __privateGet(this, _mutationObserver).disconnect();
  }
}
_intersectionObserver = new WeakMap();
_mutationObserver = new WeakMap();
_cachedRecommendations = new WeakMap();
_activeFetch = new WeakMap();
_ProductRecommendations_instances = new WeakSet();
/**
 * Load the product recommendations
 */
loadRecommendations_fn = function() {
  const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
  if (!productId || !sectionId) {
    throw new Error("Product ID and a section ID are required");
  }
  if (recommendationsPerformed === "true") {
    return;
  }
  __privateMethod(this, _ProductRecommendations_instances, fetchCachedRecommendations_fn).call(this, productId, sectionId, intent).then((result) => {
    if (!result.success) {
      if (!Shopify.designMode) {
        __privateMethod(this, _ProductRecommendations_instances, handleError_fn).call(this, new Error(`Server returned ${result.status}`));
      }
      return;
    }
    if (result.data?.trim().length) {
      this.dataset.recommendationsPerformed = "true";
      morphSection(sectionId, result.data, "hydration", { injectStylesheet: true });
    } else {
      __privateMethod(this, _ProductRecommendations_instances, handleError_fn).call(this, new Error("No recommendations available"));
    }
  }).catch((e) => {
    __privateMethod(this, _ProductRecommendations_instances, handleError_fn).call(this, e);
  });
};
fetchCachedRecommendations_fn = async function(productId, sectionId, intent) {
  const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
  const cachedResponse = __privateGet(this, _cachedRecommendations)[url];
  if (cachedResponse) {
    return { success: true, data: cachedResponse };
  }
  __privateGet(this, _activeFetch)?.abort();
  __privateSet(this, _activeFetch, new AbortController());
  try {
    const response = await fetch(url, { signal: __privateGet(this, _activeFetch).signal });
    if (!response.ok) {
      return { success: false, status: response.status };
    }
    const text = await response.text();
    __privateGet(this, _cachedRecommendations)[url] = text;
    return { success: true, data: text };
  } finally {
    __privateSet(this, _activeFetch, null);
  }
};
/**
 * Handle errors in a consistent way
 * @param {Error} error
 */
handleError_fn = function(error) {
  console.error("Product recommendations error:", error.message);
  this.classList.add("hidden");
  this.dataset.error = "Error loading product recommendations";
};
if (!customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}
